import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocketContext } from '../contexts/SocketContext';
import { useInvitationContext } from '../contexts/InvitationContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Friend {
  id: number;
  username: string;
  isOnline: boolean;
  hasPendingRequest?: boolean;
  isFriend?: boolean;
}

interface FriendRequest {
  id: number;
  fromUserId?: number;
  toUserId?: number;
  fromUsername?: string;
  toUsername?: string;
  status: string;
  createdAt: string;
}

const Friends: React.FC = () => {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<FriendRequest[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<FriendRequest[]>([]);
  const [searchUsername, setSearchUsername] = useState('');
  const [searchResults, setSearchResults] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'friends' | 'requests'>('friends');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState<Friend | null>(null);
  const { socket } = useSocketContext();
  const navigate = useNavigate();

  useEffect(() => {
    loadFriends();
    loadFriendRequests();
    
    // Listen for friend-related socket events
    if (socket) {
      socket.on('friend-request-sent', () => {
        loadFriendRequests();
      });
      
      socket.on('friend-request-accepted', () => {
        loadFriends();
        loadFriendRequests();
      });
      
      socket.on('friend-request-rejected', () => {
        loadFriendRequests();
      });
      
      socket.on('user-online', () => {
        loadFriends();
      });
      
      socket.on('user-offline', () => {
        loadFriends();
      });
      
      return () => {
        socket.off('friend-request-sent');
        socket.off('friend-request-accepted');
        socket.off('friend-request-rejected');
        socket.off('user-online');
        socket.off('user-offline');
      };
    }
  }, [socket]);

  const loadFriends = async () => {
    try {
      const response = await fetch(`${API_URL}/friends/list`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        setFriends(data.friends || []);
      }
    } catch (error) {
      console.error('Error loading friends:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFriendRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/friends/requests`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        setIncomingRequests(data.incoming || []);
        setOutgoingRequests(data.outgoing || []);
      }
    } catch (error) {
      console.error('Error loading friend requests:', error);
    }
  };

  const searchUsers = async () => {
    if (!searchUsername.trim()) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/friends/search?username=${encodeURIComponent(searchUsername)}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        setSearchResults(data.users || []);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const handleSendFriendRequest = async (userId: number) => {
    try {
      const response = await fetch(`${API_URL}/friends/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ toUserId: userId })
      });
      
      const data = await response.json();
      if (response.ok) {
        alert(data.message || 'Freundschaftsanfrage gesendet');
        loadFriendRequests();
        searchUsers(); // Refresh search results
      } else {
        alert(data.error || 'Fehler beim Senden der Anfrage');
      }
    } catch (error) {
      console.error('Error sending friend request:', error);
      alert('Fehler beim Senden der Anfrage');
    }
  };

  const handleAcceptRequest = async (requestId: number) => {
    try {
      const response = await fetch(`${API_URL}/friends/accept/${requestId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      if (response.ok) {
        alert(data.message || 'Freundschaftsanfrage angenommen');
        loadFriends();
        loadFriendRequests();
      } else {
        alert(data.error || 'Fehler beim Annehmen der Anfrage');
      }
    } catch (error) {
      console.error('Error accepting request:', error);
      alert('Fehler beim Annehmen der Anfrage');
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    try {
      const response = await fetch(`${API_URL}/friends/reject/${requestId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        loadFriendRequests();
      } else {
        const data = await response.json();
        alert(data.error || 'Fehler beim Ablehnen der Anfrage');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Fehler beim Ablehnen der Anfrage');
    }
  };

  const handleRemoveFriend = async (friendId: number) => {
    if (!window.confirm('Möchtest du diesen Freund wirklich entfernen?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/friends/${friendId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        loadFriends();
      } else {
        const data = await response.json();
        alert(data.error || 'Fehler beim Entfernen des Freundes');
      }
    } catch (error) {
      console.error('Error removing friend:', error);
      alert('Fehler beim Entfernen des Freundes');
    }
  };

  const handleInviteFriend = (friend: Friend) => {
    setSelectedFriend(friend);
    setShowInviteModal(true);
  };

  const handleInviteGameMode = async (gameMode: 'alternating' | 'race') => {
    if (!selectedFriend || !socket) return;
    
    try {
      // Get settings
      const settingsResponse = await fetch(`${API_URL}/games/settings`, {
        credentials: 'include'
      });
      const settings = await settingsResponse.json();
      
      // Send invitation via socket
      socket.emit('send-invitation', {
        username: selectedFriend.username,
        gameMode,
        wordLength: settings.wordLength || 5,
        useUmlauts: settings.useUmlauts !== undefined ? settings.useUmlauts : true
      });
      
      setShowInviteModal(false);
      setSelectedFriend(null);
      // Navigate to multiplayer lobby to see status
      navigate(`/multiplayer?mode=${gameMode}`);
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert('Fehler beim Senden der Einladung');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
        <div className="text-white text-xl">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-2 sm:p-4 overflow-x-hidden">
      <div className="max-w-2xl mx-auto pt-4 sm:pt-8">
        <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4 sm:mb-6 text-gray-800">
            Freunde
          </h1>
          
          {/* Tabs */}
          <div className="flex gap-2 mb-4 sm:mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('friends')}
              className={`flex-1 py-2 px-4 rounded-t-lg font-semibold transition-colors ${
                activeTab === 'friends'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Freunde ({friends.length})
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 py-2 px-4 rounded-t-lg font-semibold transition-colors ${
                activeTab === 'requests'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Anfragen {incomingRequests.length > 0 && (
                <span className="bg-red-500 text-white rounded-full px-2 py-0.5 text-xs ml-1">
                  {incomingRequests.length}
                </span>
              )}
            </button>
          </div>

          {activeTab === 'friends' && (
            <div className="space-y-4">
              {/* Search for new friends */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Freund hinzufügen
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchUsername}
                    onChange={(e) => {
                      setSearchUsername(e.target.value);
                      if (e.target.value.trim()) {
                        searchUsers();
                      } else {
                        setSearchResults([]);
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        searchUsers();
                      }
                    }}
                    placeholder="Username suchen..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                  />
                  <button
                    onClick={searchUsers}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg transition duration-200 min-h-[44px]"
                  >
                    Suchen
                  </button>
                </div>
                
                {/* Search results */}
                {searchResults.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {searchResults.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${user.isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
                          <span className="font-medium text-gray-800">{user.username}</span>
                        </div>
                        {!user.isFriend && !user.hasPendingRequest && (
                          <button
                            onClick={() => handleSendFriendRequest(user.id)}
                            className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-1.5 rounded-lg transition duration-200 text-sm min-h-[44px]"
                          >
                            Anfrage senden
                          </button>
                        )}
                        {user.hasPendingRequest && (
                          <span className="text-sm text-gray-500">Anfrage bereits gesendet</span>
                        )}
                        {user.isFriend && (
                          <span className="text-sm text-gray-500">Bereits Freund</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Friends list */}
              <div className="space-y-2">
                {friends.length === 0 ? (
                  <p className="text-center text-gray-600 py-8">
                    Du hast noch keine Freunde. Suche nach Benutzern, um Freundschaftsanfragen zu senden.
                  </p>
                ) : (
                  friends.map((friend) => (
                    <div
                      key={friend.id}
                      className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${friend.isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
                        <span className="font-medium text-gray-800 text-sm sm:text-base">
                          {friend.username}
                        </span>
                        {friend.isOnline && (
                          <span className="text-xs text-green-600">Online</span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {friend.isOnline && (
                          <button
                            onClick={() => handleInviteFriend(friend)}
                            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg transition duration-200 text-xs sm:text-sm min-h-[44px]"
                          >
                            Einladen
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveFriend(friend.id)}
                          className="bg-red-600 hover:bg-red-700 text-white font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg transition duration-200 text-xs sm:text-sm min-h-[44px]"
                        >
                          Entfernen
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'requests' && (
            <div className="space-y-6">
              {/* Incoming requests */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">Eingehende Anfragen</h2>
                {incomingRequests.length === 0 ? (
                  <p className="text-sm text-gray-600 py-4">Keine eingehenden Anfragen</p>
                ) : (
                  <div className="space-y-2">
                    {incomingRequests.map((request) => (
                      <div
                        key={request.id}
                        className="flex items-center justify-between p-3 sm:p-4 bg-blue-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-800">
                            {request.fromUsername}
                          </p>
                          <p className="text-xs text-gray-600">
                            {new Date(request.createdAt).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAcceptRequest(request.id)}
                            className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg transition duration-200 text-sm min-h-[44px]"
                          >
                            Annehmen
                          </button>
                          <button
                            onClick={() => handleRejectRequest(request.id)}
                            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg transition duration-200 text-sm min-h-[44px]"
                          >
                            Ablehnen
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Outgoing requests */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">Ausgehende Anfragen</h2>
                {outgoingRequests.length === 0 ? (
                  <p className="text-sm text-gray-600 py-4">Keine ausgehenden Anfragen</p>
                ) : (
                  <div className="space-y-2">
                    {outgoingRequests.map((request) => (
                      <div
                        key={request.id}
                        className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-800">
                            {request.toUsername}
                          </p>
                          <p className="text-xs text-gray-600">
                            {new Date(request.createdAt).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                        <span className="text-sm text-gray-500">Wartet auf Antwort...</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Back button */}
          <div className="mt-6 sm:mt-8">
            <button
              onClick={() => navigate('/menu')}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 text-base min-h-[44px]"
            >
              Zurück zum Menü
            </button>
          </div>
        </div>
      </div>

      {/* Invite Friend Modal */}
      {showInviteModal && selectedFriend && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {selectedFriend.username} einladen
            </h2>
            <p className="text-gray-600 mb-6">
              Wähle einen Spielmodus:
            </p>
            <div className="space-y-3">
              <button
                onClick={() => handleInviteGameMode('alternating')}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 text-base min-h-[44px]"
              >
                Gemeinsames Frördle
              </button>
              <button
                onClick={() => handleInviteGameMode('race')}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 text-base min-h-[44px]"
              >
                1 vs 1 Frördle
              </button>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setSelectedFriend(null);
                }}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 text-base min-h-[44px]"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Friends;

